import { RegionType } from "types/regionType";
import {
    Cluster,
    getStudyCycleLabel,
    getStudyGroupLabel,
    isKnownStudyCycleType,
    isKnownStudyGroupType,
    LegacyClusterInfo
} from "./clusterType";
import { ProjectSize } from "./projectType";

export interface ClusterProjectRankStats {
    rank: number;
    percentile: number;
    totalRanked: number;
    rankWithinGroup?: number;
    percentileWithinGroup?: number;
    totalRankedWithinGroup?: number;
}

/**
 * One row of the portfolio's cluster-projects table.
 *
 * Assembled client-side today; the fields past `projectName` are meant to
 * arrive precomputed from Hasura, so nothing downstream may reach past
 * this shape back into the project / scenario contexts.
 */
export interface ClusterProjectRow {
    projectId: string;
    cluster: LegacyClusterInfo | Cluster;
    /** The project's own group, which a default-group cluster spans several of. */
    studyGroup?: string;
    fuelType: string;
    /** Capped to the project's hybrid limit, so it is display-ready. */
    size: ProjectSize;
    isTracked: boolean;
    isFavorited: boolean;
    projectName?: string;
    costPerMw?: number;
    /** The project's share of the base scenario's upgrade cost, in dollars. */
    sumAllocatedCost?: number;
    /** Distinct upgrades the base scenario allocated cost to. 0 means it ran
     * the project and allocated nothing. */
    numAssignedUpgrades?: number;
    /** Absent when the base scenario did not rank the project. */
    rankStats?: ClusterProjectRankStats;
}

/**
 * Display label for a row's study group.
 *
 * `getStudyGroupLabel` returns null for the default group, which reads as "no
 * group" here. Values the enum does not know pass through as-is.
 */
export const getStudyGroupDisplayLabel = (
    region: RegionType,
    studyGroup: string | undefined
): string => {
    if (!studyGroup) {
        return "-";
    }
    if (!isKnownStudyGroupType(studyGroup)) {
        return studyGroup;
    }
    return getStudyGroupLabel(region, studyGroup) ?? "-";
};

/** Display label for a study cycle; values the enum does not know pass through. */
export const getStudyCycleDisplayLabel = (
    region: RegionType,
    studyCycle: string | undefined
): string =>
    !studyCycle
        ? "-"
        : isKnownStudyCycleType(studyCycle)
        ? getStudyCycleLabel(region, studyCycle)
        : studyCycle;

export const getClusterProjectDisplayName = (row: ClusterProjectRow): string =>
    row.projectName ? `${row.projectName} (${row.projectId})` : row.projectId;
